import React, { Component, Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import AdminLayout from './hoc/AdminLayouts/AdminLayout';

import AdminEmptyLayout from './hoc/AdminLayouts/EmptyLayout';
import StudentsLayout from './hoc/StudentsLayouts/EmptyLayout';
import Loader from 'components/UI/Spinner/Spinner';
import ReactGA from 'react-ga4';
import { ID } from 'constants/googleAnalytics';

import setAuthToken from './utils/setAuthToken';
import { parseJwt } from 'utils';
import { logOutTeacher, setCurrentTeacher } from './store/actions/authActions';
import { toggleFeatureFlag, toggleModal } from './store/actions/uiActions';
import { toggleDeveloperMode } from 'store/actions/developerActions';
import store from './store';

// favicon for platform set dynamically
import Favicon from './assets/images/favicons/favicon.ico';
import AppleFavicon from './assets/images/favicons/apple-touch-icon.png';
import Favicon32 from './assets/images/favicons/favicon-32x32.png';

import StudentAlert from 'components/StudentView/StudentAlert';

import Page404 from 'components/UI/404';
import Unauthorized from 'components/UI/Unauthorized';
import NoPermission from 'components/UI/NoPermission';
import FeatureNotAvailable from 'components/UI/FeatureNotAvailable';
import { ROUTES } from '@lsgo/lsgo-common/constants/routes';

import './GlobalAppStyle.scss';

import Auth from 'Api/Auth';

// Event listeners
import { AppCuesEvents } from './events';
import { isEmpty } from '@lsgo/lsgo-common/utils';
import { ampli } from 'ampli';
import posthog from 'posthog-js';
import { licenseTypes, roleTypes } from 'constants/options';
import ExternalEmptyLayout from 'hoc/ExternalEmptyLayout';
import Users from 'Api/Users';
import axios from 'config/axios';
import IntercomHelper from 'utils/IntercomHelper';
import { INTERCOM_APP_ID } from 'constants';

ReactGA.initialize(ID);

const RegisterComponent = lazy(() => import('containers/Auth/Register'));
const Register = (props) => (
    <Suspense fallback={<Loader />}>
        <RegisterComponent {...props} />
    </Suspense>
);

const ForgotPasswordComponent = lazy(() => import('containers/Auth/ForgotPassword'));
const ForgotPassword = (props) => (
    <Suspense fallback={<Loader />}>
        <ForgotPasswordComponent {...props} />
    </Suspense>
);

const ResetPasswordComponent = lazy(() => import('containers/Auth/ResetPassword'));
const ResetPassword = (props) => (
    <Suspense fallback={<Loader />}>
        <ResetPasswordComponent {...props} />
    </Suspense>
);

const ProfileComponent = lazy(() => import('containers/Teacher/Profile'));
const Profile = (props) => (
    <Suspense fallback={<Loader />}>
        <ProfileComponent {...props} />
    </Suspense>
);

const ResourceComponent = lazy(() => import('containers/Resource/Resource'));
const Resource = (props) => (
    <Suspense fallback={<Loader />}>
        <ResourceComponent {...props} />
    </Suspense>
);

const LoginComponent = lazy(() => import('containers/Auth/Login'));
const Login = (props) => (
    <Suspense fallback={<Loader />}>
        <LoginComponent {...props} />
    </Suspense>
);

const QuickWeatherReportComponent = lazy(() => import('containers/WeatherReport/QuickWeatherReport'));
const QuickWeatherReport = (props) => (
    <Suspense fallback={<Loader />}>
        <QuickWeatherReportComponent {...props} />
    </Suspense>
);

const MyClassesComponent = lazy(() => import('containers/Classes/MyClasses'));
const MyClasses = (props) => (
    <Suspense fallback={<Loader />}>
        <MyClassesComponent {...props} />
    </Suspense>
);

const DeveloperDashboardComponent = lazy(() => import('containers/Developer/DeveloperDashboard'));
const DeveloperDashboard = (props) => (
    <Suspense fallback={<Loader />}>
        <DeveloperDashboardComponent {...props} />
    </Suspense>
);

const AdvancedSettingsDashboardComponent = lazy(() => import('containers/AdvancedSettings/AdvancedSettingsDashboard'));
const AdvancedSettingsDashboard = (props) => (
    <Suspense fallback={<Loader />}>
        <AdvancedSettingsDashboardComponent {...props} />
    </Suspense>
);

const FormsComponent = lazy(() => import('containers/Forms/Forms'));
const Forms = (props) => (
    <Suspense fallback={<Loader />}>
        <FormsComponent {...props} />
    </Suspense>
);

const AboutComponent = lazy(() => import('containers/About/About'));
const About = (props) => (
    <Suspense fallback={<Loader />}>
        <AboutComponent {...props} />
    </Suspense>
);

const LtiRouterComponent = lazy(() => import('containers/LTI/Router'));
const LtiRouter = (props) => (
    <Suspense fallback={<Loader />}>
        <LtiRouterComponent {...props} />
    </Suspense>
);

const SchoolAdminComponent = lazy(() => import('routing/SchoolAdmin'));
const SchoolAdmin = (props) => (
    <Suspense fallback={<Loader />}>
        <SchoolAdminComponent {...props} />
    </Suspense>
);

const TeacherWellbeingComponent = lazy(() => import('containers/Resource/TeacherWellbeing'));
const TeacherWellbeing = (props) => (
    <Suspense fallback={<Loader />}>
        <TeacherWellbeingComponent {...props} />
    </Suspense>
);

const RollCallComponent = lazy(() => import('containers/WeatherReport/RollCall'));
const RollCall = (props) => (
    <Suspense fallback={<Loader />}>
        <RollCallComponent {...props} />
    </Suspense>
);

const LeaderBoardComponent = lazy(() => import('containers/Schools/Leaderboard'));
const LeaderBoard = (props) => (
    <Suspense fallback={<Loader />}>
        <LeaderBoardComponent {...props} />
    </Suspense>
);

const GoogleRegistrationComponent = lazy(() => import('containers/Auth/GoogleRegistration'));
const GoogleRegistration = (props) => (
    <Suspense fallback={<Loader />}>
        <GoogleRegistrationComponent {...props} />
    </Suspense>
);

const TrialRegisterComponent = lazy(() => import('containers/Auth/TrialRegister'));
const TrialRegister = () => (
    <Suspense fallback={<Loader />}>
        <TrialRegisterComponent />
    </Suspense>
);

const SentralComponent = lazy(() => import('containers/Sentral/Sentral'));
const Sentral = () => (
    <Suspense fallback={<Loader />}>
        <SentralComponent />
    </Suspense>
);

const SchoolBytesComponent = lazy(() => import('containers/ExternalProviders/SchoolBytes/SchoolBytes'));
const SchoolBytes = () => (
    <Suspense fallback={<Loader />}>
        <SchoolBytesComponent />
    </Suspense>
);

const SentralStudentLayoutComponent = lazy(() => import('containers/Sentral/SentralStudentLayout'));
const SentralStudentLayout = () => (
    <Suspense fallback={<Loader />}>
        <SentralStudentLayoutComponent />
    </Suspense>
);

const SchoolBytesStudentLayoutComponent = lazy(() => import('containers/ExternalProviders/SchoolBytes/StudentsLayout'));
const SchoolBytesStudentLayout = () => (
    <Suspense fallback={<Loader />}>
        <SchoolBytesStudentLayoutComponent />
    </Suspense>
);

const SentralInactiveComponent = lazy(() => import('containers/Sentral/InactiveTrial'));
const InactiveTrial = () => (
    <Suspense fallback={<Loader />}>
        <SentralInactiveComponent />
    </Suspense>
);

const ExternalExecComponent = lazy(() => import('containers/Schools/ExecutiveReport'));
const ExternalExec = () => (
    <Suspense fallback={<Loader />}>
        <ExternalExecComponent />
    </Suspense>
);

const OTPComponent = lazy(() => import('components/Auth/OTP'));
const OTP = () => (
    <Suspense fallback={<Loader />}>
        <OTPComponent />
    </Suspense>
);

const GlobalSearchComponent = lazy(() => import('components/GlobalSearch'));
const GlobalSearch = () => (
    <Suspense fallback={<Loader />}>
        <GlobalSearchComponent />
    </Suspense>
);

const StudentGroupsComponent = lazy(() => import('components/StudentGroups'));
const StudentGroups = () => (
    <Suspense fallback={<Loader />}>
        <StudentGroupsComponent />
    </Suspense>
);

const TestComp = () => {
    return store.getState().auth.isAuthenticated ? (
        <ExternalExec />
    ) : (
        <div>Request Failed. Cannot find authentication data for HubSpot dashboard.</div>
    );
};

const SecureFileUploadComponent = lazy(() => import('components/SecureFileUpload'));

const SecureFileUpload = () => (
    <Suspense fallback={<Loader />}>
        <SecureFileUploadComponent />
    </Suspense>
);

const ReportsSelectionCardComponent = lazy(() => import('containers/Schools/ReportsSelectionCard'));
const ReportsSelectionCard = (props) => (
    <Suspense fallback={<Loader />}>
        <ReportsSelectionCardComponent {...props} />
    </Suspense>
);

const TestAuthComp = () => {
    // localStorage.removeItem('lsgoRT');
    const { tok } = useParams();
    localStorage.setItem('lsgoRT', tok);
    window.location.href = '/hs/external';
    return <div>Hi</div>;
};

class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hubspot: false,
            intercom: true,
            hubspotScript: false,
            logoutWarningTimer: null,
            authLoop: true,
        };
        // this.loadHubSpot = this.loadHubSpot.bind(this);
        // this.deloadHubspot = this.deloadHubspot.bind(this);
        //this.loadIntercom = this.loadIntercom.bind(this);
        this.handleVideoQuery = this.handleVideoQuery.bind(this);
    }

    static propTypes = {
        location: PropTypes.object.isRequired,
    };

    handleVideoQuery() {
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search));
        if (queryParams['video.id'] && !store.getState().ui.showModal) {
            store.dispatch(
                toggleModal(true, 'video_activity_modal', {
                    video: {
                        id: queryParams['video.id'],
                        name: queryParams['video.name'],
                        timestamp: queryParams['video.timestamp'],
                    },
                })
            );
        }
        if (queryParams['setIsDeveloper']) {
            store.dispatch(toggleDeveloperMode);
        }
    }

    componentDidMount() {
        this.handleVideoQuery();

        window.location.hostname.includes('lifeskillsgo') &&
            ampli.load({
                environment: window.location.hostname.includes('staging') ? 'development' : 'production',
            });

        const fetchApiToken = () => {
            // this.setState({ authLoop: true });
            Auth.userV2({
                history: { push: this.props.navigate, location: this.props.location },
                redirectUri: this.props.location.search,
                lsgoRT: localStorage.getItem('lsgoRT'),
            })
                .then(async ({ token, intercomToken }) => {
                    if (token) {
                        axios.get(`/api/v4/schools/feature-flag/dashboardV5`)?.then((r) => {
                            const accessAllowed = r?.data?.accessAllowed;
                            store.dispatch(toggleFeatureFlag('dashboardV5', true, !!accessAllowed));
                        });
                        const decoded = await parseJwt(token.replace('Bearer ', ''));
                        if (decoded.id) {
                            try {
                                if (
                                    !isEmpty(decoded.tfaEnabled) &&
                                    decoded.tfaEnabled &&
                                    !isEmpty(decoded.tfaAuthenticated) &&
                                    !decoded.tfaAuthenticated
                                ) {
                                    console.log('pushing for tfa');
                                    this.props.navigate('/tfa/verify/');
                                } else {
                                    const profile = await Users.getUserProfile(decoded.id);

                                    // let schoolRegion = '';
                                    // const intercomGroup = await Users.getIntercomGroup({ id: profile?.data?.id });
                                    // schoolRegion = intercomGroup?.data?.schoolRegion;

                                    sessionStorage.setItem('isIntercom', true);
                                    // const userHash = await Users.getIntercomHash(profile.data?.email);
                                    // IntercomHelper.shutdown();

                                    if (localStorage.getItem('intercomToken') !== null) {
                                        IntercomHelper.shutdown();
                                        IntercomHelper.load({
                                            app_id: INTERCOM_APP_ID,
                                            email: profile.data?.email,
                                            name: profile.data?.firstName,
                                            intercom_user_jwt: localStorage.getItem('intercomToken'),
                                        });
                                    } else {
                                        IntercomHelper.shutdown();
                                        IntercomHelper.load({
                                            app_id: INTERCOM_APP_ID,
                                            email: profile.data?.email,
                                            name: profile.data?.firstName,
                                            intercom_user_jwt: intercomToken,
                                        });
                                    }

                                    ampli.identify(profile.data.id, {
                                        license: licenseTypes[profile.data.groups.licenseId],
                                        role: roleTypes[profile.data.groups.roleId],
                                        payment_status: profile.data.groups.paymentStatus || 'free',
                                        group_id: profile.data.groups.groupId,
                                        school_id: profile.data.schoolId,
                                    });

                                    posthog.identify(profile.data.id, {
                                        user_id: profile.data.id,
                                        school_id: profile.data.schoolId,
                                        role: roleTypes[profile.data.groups.roleId],
                                        license: licenseTypes[profile.data.groups.licenseId],
                                        payment_status: profile.data.groups.paymentStatus || 'free',
                                    });
                                    if (!profile.data.name) profile.data.name = profile.data.firstName;
                                    store.dispatch(setCurrentTeacher({ ...profile.data, alias: decoded.alias }));
                                }
                            } catch (e) {
                                console.log(e);
                            }
                        } else if (store.getState().auth.isAuthenticated) store.dispatch(logOutTeacher());
                    }
                    this.setState({ authLoop: false });
                })
                .catch((err) => this.setState({ authLoop: false }));
        };
        fetchApiToken();

        this.authenticationLoop = setInterval(() => {
            if (store.getState().auth.isAuthenticated) fetchApiToken();
        }, 1.5 * 60 * 60 * 1000); // Refresh token every 1.5 hours

        document.getElementById('favicon').href = Favicon;
        document.getElementById('favicon32').href = Favicon32;
        document.getElementById('faviconApple').href = AppleFavicon;
        const path = this.props.location.pathname;

        //check if admin has logged in as teacher
        if (this.props.location.pathname.indexOf('/teacher/Bearer') !== -1) {
            const token = this.props.location.pathname.split('/')[2];
            // set token
            setAuthToken(token);

            parseJwt(token.replace('Bearer ', '')).then((decoded) => {
                Users.getUserProfile(decoded.id).then((profile) => {
                    if (!profile.data.name) profile.data.name = profile.data.firstName;
                    store.dispatch(setCurrentTeacher({ ...profile.data, alias: decoded.alias }));
                });

                // // redirect to /teacher
                // this.props.history.push('/teacher');

                return;
            });
        }
        const isIntercom = true;

        if (isIntercom && !path.includes('teacher/register') && !path.includes('teacher/verify')) {
            if (localStorage.getItem('intercomToken') !== null) {
                IntercomHelper.load({
                    app_id: INTERCOM_APP_ID,
                    intercom_user_jwt: localStorage.getItem('intercomToken'),
                });
            } else {
                IntercomHelper.load();
            }
        }
    }

    componentWillUnmount() {
        clearInterval(this.authenticationLoop);
    }

    componentDidUpdate(prevProps) {
        const {
            location: { pathname },
        } = this.props;
        const previousLocation = prevProps.location.pathname;

        if (pathname !== previousLocation) {
            window.Appcues && window.Appcues.page();
        }
        if (this.state.authLoop && store.getState().auth.isAuthenticated) {
            this.setState({ authLoop: false });
        }
    }

    render() {
        return this.state.authLoop ? (
            <Loader />
        ) : (
            <>
                <Routes>
                    <Route path="/index.html" element={<Navigate to="/" replace />} />
                    {/* External/Public Routes */}
                    <Route
                        path={ROUTES.EXTERNAL.FORMS.path}
                        element={<ExternalEmptyLayout embed component={Forms} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.ABOUT.path}
                        element={<ExternalEmptyLayout embed component={About} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.LTI.path}
                        element={<ExternalEmptyLayout embed withHeader component={LtiRouter} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.SENTRAL_INACTIVE.path}
                        element={<ExternalEmptyLayout embed component={InactiveTrial} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.SCHOOLBYTES_INACTIVE.path}
                        element={<ExternalEmptyLayout embed component={InactiveTrial} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.HS_EXTERNAL.path}
                        element={<ExternalEmptyLayout hsExternal component={TestComp} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.HS_AUTH_ONESHOT.path}
                        element={<ExternalEmptyLayout hsExternal component={TestAuthComp} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.ROLLOVER_SECURE_FILE_UPLOAD.path}
                        element={<ExternalEmptyLayout embed component={SecureFileUpload} />}
                    />
                    <Route
                        path="/school/admin/reportsV2"
                        element={<ExternalEmptyLayout embed component={ReportsSelectionCard} />}
                    />

                    {/* Teacher Routes */}
                    <Route path={ROUTES.TEACHER.RESOURCE.path} element={<AdminLayout component={Resource} />} />
                    <Route path={ROUTES.TEACHER.PROFILE.path} element={<AdminLayout component={Profile} />} />
                    <Route
                        path={ROUTES.TEACHER.WELLBEING.path}
                        element={<AdminLayout component={TeacherWellbeing} />}
                    />
                    <Route path={ROUTES.TEACHER.LEADERBOARD.path} element={<AdminLayout component={LeaderBoard} />} />
                    <Route path={ROUTES.TEACHER.DASHBOARD.path} element={<AdminLayout component={MyClasses} />} />
                    <Route path={ROUTES.TEACHER.CLASSES.path} element={<AdminLayout component={MyClasses} />} />
                    <Route
                        path={ROUTES.TEACHER.QUICK_WEATHER_REPORT.path}
                        element={<StudentsLayout component={QuickWeatherReport} />}
                    />
                    <Route path={ROUTES.TEACHER.ROLLCALL.path} element={<StudentsLayout component={RollCall} />} />

                    {/* School Admin Routes */}
                    <Route path={ROUTES.SCHOOL.DASHBOARD.path} element={<AdminLayout component={SchoolAdmin} />} />

                    {/* Auth Routes */}
                    <Route path={ROUTES.AUTH.LOGIN.path} element={<AdminEmptyLayout component={Login} />} />
                    <Route path={ROUTES.AUTH.TRIAL.path} element={<AdminEmptyLayout component={TrialRegister} />} />
                    <Route path={ROUTES.AUTH.REGISTER.path} element={<AdminEmptyLayout component={Register} />} />
                    <Route
                        path={ROUTES.AUTH.GOOGLE_REGISTRATION.path}
                        element={<AdminEmptyLayout component={GoogleRegistration} />}
                    />
                    <Route
                        path={ROUTES.AUTH.FORGOT_PASSWORD.path}
                        element={<AdminEmptyLayout component={ForgotPassword} />}
                    />
                    <Route
                        path={ROUTES.AUTH.RESET_PASSWORD.path}
                        element={<AdminEmptyLayout component={ResetPassword} />}
                    />
                    <Route path={ROUTES.AUTH.TFA_VERIFY.path} element={<AdminEmptyLayout component={OTP} />} />
                    <Route path={ROUTES.AUTH.UNAUTHORIZED.path} element={<Unauthorized />} />
                    <Route path={ROUTES.AUTH.NO_PERMISSION.path} element={<NoPermission />} />
                    <Route path={ROUTES.AUTH.FEATURE_NOT_AVAILABLE.path} element={<FeatureNotAvailable />} />

                    {/* SSO Routes */}
                    <Route
                        path={ROUTES.EXTERNAL.SENTRAL.children.ATTENDANCE.path}
                        element={<StudentsLayout sentralUnsafeRoute component={SentralStudentLayout} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.SENTRAL.children.CHECKIN.path}
                        element={<StudentsLayout sentralUnsafeRoute component={SentralStudentLayout} />}
                    />
                    <Route
                        path={`${ROUTES.EXTERNAL.SENTRAL.path}/*`}
                        element={<ExternalEmptyLayout sentralUnsafeRoute component={Sentral} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.SCHOOLBYTES.children.ATTENDANCE.path}
                        element={<StudentsLayout schoolBytesUnsafeRoute component={SchoolBytesStudentLayout} />}
                    />
                    <Route
                        path={ROUTES.EXTERNAL.SCHOOLBYTES.children.CHECKIN.path}
                        element={<StudentsLayout schoolBytesUnsafeRoute component={SchoolBytesStudentLayout} />}
                    />
                    <Route
                        path={`${ROUTES.EXTERNAL.SCHOOLBYTES.path}/*`}
                        element={<ExternalEmptyLayout schoolBytesUnsafeRoute component={SchoolBytes} />}
                    />

                    {/* Other Routes */}
                    <Route
                        path={ROUTES.OTHER.DEVELOPER.path}
                        element={<AdminLayout component={DeveloperDashboard} />}
                    />
                    <Route
                        path={ROUTES.OTHER.ADVANCED_SETTINGS.path}
                        element={<AdminLayout component={AdvancedSettingsDashboard} />}
                    />
                    <Route
                        path={ROUTES.OTHER.STUDENT_GROUPS.path}
                        element={<AdminLayout component={StudentGroups} />}
                    />
                    <Route path={ROUTES.OTHER.PAGE_404.path} element={<Page404 />} />
                </Routes>
                <StudentAlert />
                <AppCuesEvents />
                <GlobalSearch />
            </>
        );
    }
}

const AppWrapper = () => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        ReactGA.set({ page: location.pathname });
        ReactGA.pageview(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        if (location.pathname !== undefined) {
            window.Appcues && window.Appcues.page();
        }
    }, [location.pathname]);

    return <App location={location} navigate={navigate} />;
};

export default AppWrapper;
